// Plan 17-07 Task 1 — Settings re-skin (POLISH-13).
//
// Prototype-driven re-skin: `rg-screen settings` shell + stack of `.card`
// panels (Display / Language / Notifications / Accessibility / Account /
// About). Motion + Language pickers wired inline via the shared <Seg>
// primitive (POLISH-09 radiogroup with arrow-key nav). All inline raw
// literals replaced with var(--*) tokens.
//
// Plan-17-07 history note: the standalone LanguageToggle.tsx and
// ReduceMotionToggle.tsx wrappers had a single consumer (this file) and have
// been DELETED — their state wiring is inlined here on top of the Seg
// primitive, matching the prototype's card-per-section layout. Phase 16
// NotificationsCard + Phase 18 a11y toggles (FontScale / Typography /
// Contrast / AboutFonts) remain delegated as child components.
//
// Theme picker REMOVED (folk-art Locked Decision #1 — single canonical theme,
// no legacy look reachable). Motion picker writes to $motionPref (OR-union with
// $osReduced via the $reducedMotion computed atom mirrored onto <html
// data-motion>). Language picker writes to $locale (IntlContextProvider
// reloads the messages dictionary live).

import { useStore } from '@nanostores/react'
import { type JSX, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { useAuth } from '../../auth/AuthProvider.js'
import { useDocumentTitle } from '../../i18n/useDocumentTitle.js'
import { useRouter } from '../../router/router.js'
import { $locale, type Locale } from '../../stores/locale.js'
import { $motionPref, type MotionPref } from '../../stores/motion.js'
import { DeleteAccountModal } from '../account/DeleteAccountModal.js'
import { Btn } from '../components/Btn.js'
import { Screen, ScreenHeader } from '../components/Screen.js'
import { Seg } from '../components/Seg.js'
import { AboutFonts } from './AboutFonts.js'
import { ContrastToggle } from './ContrastToggle.js'
import { FontScaleToggle } from './FontScaleToggle.js'
import { NotificationsCard } from './NotificationsCard.js'
import { SoundToggle } from './SoundToggle.js'
import { TypographyToggle } from './TypographyToggle.js'

// Section caption, matching the prototype <Label>: faint-ink Fredoka, wide
// uppercase tracking.
const sectionHeading: React.CSSProperties = {
  margin: 0,
  fontFamily: 'Fredoka, sans-serif',
  fontWeight: 600,
  fontSize: 'var(--font-size-14)',
  color: 'var(--color-text-tertiary)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
}

const cardStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
}

const rowStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
}

const rowLabelStyle: React.CSSProperties = {
  fontFamily: 'Fredoka, sans-serif',
  fontWeight: 700,
  fontSize: 'var(--font-size-16)',
  color: 'var(--color-text-primary)',
}

export function SettingsScreen(): JSX.Element {
  useDocumentTitle('doc.title.settings')
  const router = useRouter()
  const auth = useAuth()
  const intl = useIntl()
  const locale = useStore($locale)
  const motionPref = useStore($motionPref)
  const [showDelete, setShowDelete] = useState(false)

  const motionLabel = intl.formatMessage({
    id: 'settings.reduce_motion.label',
    defaultMessage: 'Reduce motion',
  })
  const languageLabel = intl.formatMessage({
    id: 'settings.language.label',
    defaultMessage: 'Language',
  })

  return (
    <Screen
      name="settings"
      gutter={18}
      contentStyle={{ gap: 'var(--space-5)', alignItems: 'center' }}
      header={
        <ScreenHeader
          title={
            <FormattedMessage id="settings.title" defaultMessage="Settings" />
          }
          onBack={() => router.navigate('/lobby')}
          backLabel={intl.formatMessage({
            id: 'nav.back',
            defaultMessage: 'Back',
          })}
        />
      }
    >
      <section
        className="card"
        aria-labelledby="settings-display-heading"
        data-section="display"
        style={cardStyle}
      >
        <h2 id="settings-display-heading" style={sectionHeading}>
          <FormattedMessage
            id="settings.section.display"
            defaultMessage="Display"
          />
        </h2>
        <div style={rowStyle}>
          <span style={rowLabelStyle}>{motionLabel}</span>
          <Seg<MotionPref>
            ariaLabel={motionLabel}
            value={motionPref}
            onChange={(next) => $motionPref.set(next)}
            testId="motion-picker"
            options={[
              {
                value: 'auto',
                label: intl.formatMessage({
                  id: 'settings.reduce_motion.option.auto',
                  defaultMessage: 'Auto',
                }),
              },
              {
                value: 'reduced',
                label: intl.formatMessage({
                  id: 'settings.reduce_motion.option.reduced',
                  defaultMessage: 'Reduce',
                }),
              },
              {
                value: 'full',
                label: intl.formatMessage({
                  id: 'settings.reduce_motion.option.full',
                  defaultMessage: 'Full',
                }),
              },
            ]}
          />
        </div>
      </section>

      <section
        className="card"
        aria-labelledby="settings-language-heading"
        data-section="language"
        style={cardStyle}
      >
        <h2 id="settings-language-heading" style={sectionHeading}>
          <FormattedMessage
            id="settings.section.language"
            defaultMessage="Language"
          />
        </h2>
        <Seg<Locale>
          ariaLabel={languageLabel}
          value={locale}
          onChange={(next) => $locale.set(next)}
          testId="language-picker"
          options={[
            {
              value: 'en',
              label: intl.formatMessage({
                id: 'settings.language.option.en',
                defaultMessage: 'English',
              }),
            },
            {
              value: 'sv',
              label: intl.formatMessage({
                id: 'settings.language.option.sv',
                defaultMessage: 'Svenska',
              }),
            },
          ]}
        />
      </section>

      <section
        className="card"
        aria-labelledby="settings-sound-heading"
        data-section="sound"
        style={cardStyle}
      >
        <h2 id="settings-sound-heading" style={sectionHeading}>
          <FormattedMessage
            id="settings.section.sound"
            defaultMessage="Sound"
          />
        </h2>
        <SoundToggle />
      </section>

      <NotificationsCard />

      <section
        className="card"
        aria-labelledby="settings-accessibility-heading"
        data-section="accessibility"
        style={cardStyle}
      >
        <h2 id="settings-accessibility-heading" style={sectionHeading}>
          <FormattedMessage
            id="settings.section.accessibility"
            defaultMessage="Accessibility"
          />
        </h2>
        <FontScaleToggle />
        <TypographyToggle />
        <ContrastToggle />
        <AboutFonts />
      </section>

      <section
        className="card"
        aria-labelledby="settings-account-heading"
        data-section="account"
        style={cardStyle}
      >
        <h2 id="settings-account-heading" style={sectionHeading}>
          <FormattedMessage
            id="settings.section.account"
            defaultMessage="Account"
          />
        </h2>
        <Btn
          kind="ghost"
          size="md"
          block
          onClick={() => auth.logout()}
          testId="settings-signout"
        >
          <FormattedMessage
            id="settings.account.signout_button"
            defaultMessage="Sign out"
          />
        </Btn>
        <Btn
          kind="primary"
          size="md"
          block
          onClick={() => setShowDelete(true)}
          testId="settings-delete-account"
        >
          <FormattedMessage
            id="settings.account.delete_button"
            defaultMessage="Delete account"
          />
        </Btn>
      </section>

      <Btn
        kind="ghost"
        size="sm"
        onClick={() => router.navigate('/about')}
        testId="settings-about-link"
      >
        <FormattedMessage
          id="settings.about.link"
          defaultMessage="About this game"
        />
      </Btn>

      <DeleteAccountModal
        open={showDelete}
        onClose={() => setShowDelete(false)}
      />
    </Screen>
  )
}
